import { useMemo } from "react";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  Archive,
  BellRing,
  BookOpenCheck,
  CalendarClock,
  CheckCircle2,
  CirclePlus,
  Flag,
  History,
  LogIn,
  Target,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/endpoints";
import {
  ActiveFilters,
  ColumnVisibility,
  DataTable,
  TablePagination,
} from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { type FilterBinding, useUrlColumnFilters } from "@/hooks/use-url-column-filters";
import { useUrlPagination } from "@/hooks/use-url-pagination";
import { formatDateTime, formatShortDay } from "@/lib/format";
import { readEnum } from "@/lib/url-params";
import { cn } from "@/lib/utils";
import { ACTIVITY_GROUPS, type ActivityItem, type ActivityType } from "@/types/api";

const BINDINGS: FilterBinding[] = [{ columnId: "type", param: "group", allowed: ACTIVITY_GROUPS }];

const ICONS: Record<ActivityType, { icon: LucideIcon; tone: string }> = {
  LOGIN: { icon: LogIn, tone: "bg-tile-sky text-tile-sky-strong" },
  TASK_CREATED: { icon: CirclePlus, tone: "bg-tile-amber text-tile-amber-strong" },
  TASK_COMPLETED: { icon: CheckCircle2, tone: "bg-tile-mint text-tile-mint-strong" },
  TASK_POSTPONED: { icon: CalendarClock, tone: "bg-tile-violet text-tile-violet-strong" },
  TASK_CANCELLED: { icon: XCircle, tone: "bg-destructive/10 text-destructive" },
  GOAL_CREATED: { icon: Target, tone: "bg-tile-coral text-tile-coral-strong" },
  GOAL_COMPLETED: { icon: Flag, tone: "bg-tile-mint text-tile-mint-strong" },
  GOAL_ARCHIVED: { icon: Archive, tone: "bg-muted text-muted-foreground" },
  DAILY_REVIEW: { icon: BookOpenCheck, tone: "bg-tile-violet text-tile-violet-strong" },
  WEEKLY_REVIEW: { icon: BookOpenCheck, tone: "bg-tile-violet text-tile-violet-strong" },
  PERIOD_REVIEW: { icon: BookOpenCheck, tone: "bg-tile-violet text-tile-violet-strong" },
  NOTIFICATION_READ: { icon: BellRing, tone: "bg-tile-sky text-tile-sky-strong" },
};

/**
 * Harakatlar jurnali — alohida audit jadvali yo'q, backend mavjud vaqt
 * belgilaridan yig'adi (kirish, vazifa, maqsad, sharh, o'qilgan xabar).
 * Guruh filtri sarlavhada, sahifa va hajm manzilda.
 */
export function UserActivityTab({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { page, limit, setPage, setLimit } = useUrlPagination();
  const { columnFilters, onColumnFiltersChange } = useUrlColumnFilters(BINDINGS);
  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility("user-activity");
  const filters = { page, limit, group: readEnum(params, "group", ACTIVITY_GROUPS) };
  const log = useQuery({
    queryKey: queryKeys.userActivity(userId, filters),
    queryFn: () => adminApi.userActivity(userId, filters),
    placeholderData: keepPreviousData,
  });

  const columns = useActivityColumns();
  const table = useReactTable({
    data: log.data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    state: {
      columnFilters,
      columnVisibility,
      pagination: { pageIndex: page - 1, pageSize: limit },
    },
    onColumnFiltersChange,
    onColumnVisibilityChange,
    manualFiltering: true,
    manualPagination: true,
    enableSorting: false,
    pageCount: log.data?.meta.totalPages ?? 0,
  });

  return (
    <section className="flex h-[70svh] min-h-96 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ActiveFilters table={table} />
        <div className="ml-auto">
          <ColumnVisibility table={table} />
        </div>
      </div>
      <DataTable
        table={table}
        isLoading={log.isPending}
        isFetching={log.isFetching}
        error={log.error}
        onRetry={() => void log.refetch()}
        empty={
          <EmptyState
            icon={History}
            title={t("activity.emptyTitle")}
            text={filters.group ? t("activity.emptyFiltered") : t("activity.emptyText")}
          />
        }
      />
      {log.data && log.data.meta.total > 0 ? (
        <TablePagination
          page={page}
          limit={limit}
          total={log.data.meta.total}
          totalPages={log.data.meta.totalPages}
          onPage={setPage}
          onLimit={setLimit}
        />
      ) : null}
    </section>
  );
}

function useActivityColumns(): ColumnDef<ActivityItem>[] {
  const { t } = useTranslation();
  const lang = currentLanguage();

  return useMemo<ColumnDef<ActivityItem>[]>(() => {
    const detail = (item: ActivityItem): string | null => {
      if (!item.detail) return null;
      if (item.type === "LOGIN") return `IP ${item.detail}`;
      if (item.type.startsWith("GOAL_")) return t(`activity.goalTypes.${item.detail}`);
      if (item.type === "NOTIFICATION_READ") return t(`deliveries.types.${item.detail}`);
      return t("activity.forDate", { date: formatShortDay(item.detail, lang) });
    };
    const title = (item: ActivityItem): string | null => {
      if (item.type === "PERIOD_REVIEW" && item.title) return t(`activity.periods.${item.title}`);
      if (item.type === "LOGIN") return item.title ?? t("activity.unknownDevice");
      return item.title;
    };
    return [
      {
        id: "at",
        accessorKey: "at",
        header: t("activity.at"),
        enableHiding: false,
        meta: { label: t("activity.at"), width: 190 },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs whitespace-nowrap tabular-nums">
            {formatDateTime(row.original.at, lang)}
          </span>
        ),
      },
      {
        id: "type",
        accessorKey: "type",
        header: t("activity.event"),
        enableHiding: false,
        meta: {
          label: t("activity.event"),
          width: 230,
          filterVariant: "select",
          filterOptions: ACTIVITY_GROUPS.map(group => ({
            value: group,
            label: t(`activity.groups.${group}`),
          })),
        },
        cell: ({ row }) => {
          const { icon: Icon, tone } = ICONS[row.original.type];
          return (
            <span className="flex items-center gap-2">
              <span className={cn("grid size-7 shrink-0 place-items-center rounded-lg", tone)}>
                <Icon className="size-3.5" aria-hidden />
              </span>
              <span className="truncate font-medium">
                {t(`activity.types.${row.original.type}`)}
              </span>
            </span>
          );
        },
      },
      {
        id: "title",
        accessorKey: "title",
        header: t("activity.object"),
        meta: { label: t("activity.object"), width: 320 },
        cell: ({ row }) => <span className="block truncate">{title(row.original) ?? "—"}</span>,
      },
      {
        id: "detail",
        accessorKey: "detail",
        header: t("activity.detail"),
        meta: { label: t("activity.detail"), width: 200 },
        cell: ({ row }) => (
          <span className="text-muted-foreground block truncate text-xs">
            {detail(row.original) ?? "—"}
          </span>
        ),
      },
    ];
  }, [t, lang]);
}
